<template>
  <div id="living-images">
    <!-- loading screen -->
    <transition name="fade" v-if="!notSupported.state">
      <div class="text-center">
        <v-dialog
          :value="!cameraLoaded"
          hide-overlay
          persistent
          width="300"
        >
          <v-card
            color="pink"
            dark
            class="pt-2"
          >
            <v-card-text>
              <p class="mb-2 white--text text-center">Living Images werden geladen ...</p>
              <v-progress-linear
                indeterminate
                color="white"
                class="mb-0"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>
    </transition>

    <!-- Browser Not Supported -->
    <transition name="fade">
      <modal-not-supported v-model="notSupported.showModal" />
    </transition>

    <!-- A-Frame scene -->
    <a-scene
      id="scene"
      ref="scene"
      v-if="arjsLoaded"
      embedded
      :arjs="`debugUIEnabled: false;
        patternRatio: ${activeLivingImage ? activeLivingImage.patternRatio : 0.9}`"
      vr-mode-ui="enabled: false"
      cursor="fuse: false; rayOrigin: mouse"
      raycaster="objects: .clickable"
    >
      <!-- assets to be used -->
      <a-assets timeout="10000" @loaded="onAssetsLoaded" @timeout="onAssetsTimeout">
        <video
          :id="`v-${livingImage.id}`"
          :src="livingImage.video"
          webkit-playsinline
          playsinline
          crossorigin="anonymous"
          :muted="muted"
          v-for="livingImage in livingImages"
          :key="livingImage.id"
        >
          Ihr Browser unterstützt das Videeo leider nicht. Versuchen Sie es mit einem anderen noch mal.
        </video>
      </a-assets>

      <!-- markers -->
      <a-marker
        :id="`m-${livingImage.id}`"
        emitevents="true"
        preset="custom"
        type="pattern"
        :url="livingImage.pattern"
        v-for="livingImage in livingImages"
        :key="livingImage.id"
        smooth="false"
      >
        <!-- video objects -->
        <a-video
          :id="`a-${livingImage.id}`"
          :class="livingImage.data.active ? 'clickable' : ''"
          :src="'#v-' + livingImage.id"
          position="0 0 0"
          rotation="270 0 0"
          height="0.9"
          width="0.9"
          clickable
          :living-image="`title: ${livingImage.title}; fadeTime: ${fadeTime}; loadingOverlay: #l-${livingImage.id}; muted: ${muted};`"
          material="opacity: 0"
          :animation__fade-in="`property: components.material.material.opacity;
            from: 0;
            to: 1;
            dur: ${fadeTime};
            startEvents: fade-in`"
          :animation__fade-out="`property: components.material.material.opacity;
            from: 1;
            to: 0;
            dur: ${fadeTime};
            startEvents: fade-out`"
        />

        <!-- loading overlay -->
        <a-plane
          :id="'l-' + livingImage.id"
          src="/images/loading_overlay_smaller.png"
          position="0 0.03 0"
          width="0.6"
          height="0.6"
          material="transparent: true"
          :animation__rotation="`property: rotation;
            from: 270 0 0;
            to: 270 0 360;
            dir: reverse;
            loop: true;
            dur: 700;
            easing: linear;
            autoplay: true`"
        />
      </a-marker>

      <!-- virtual camera -->
      <a-entity id="camera" camera />
    </a-scene>

    <!-- bottom button bar -->
    <div id="bottom-bar" v-if="cameraLoaded">
      <!-- console messages -->
      <!-- <div id="console">
        <div id="console-content" ref="console-content">
          <span v-for="(msg, key) in consoleMessages" :key="key">{{ msg }}<br></span>
        </div>
        <button @click="consoleMessages = []">Leeren</button>
      </div> -->

      <!-- play/pause button -->
      <v-btn
        @click="onPlayBtn"
        :disabled="!activeLivingImage
          || activeLivingImage.data.loading
          || activeLivingImage.data.ended
          || activeLivingImage.data.fading"
        id="play-button"
        class="bottom-bar-btn"
        fab
      >
        <!-- <img
          src="@/assets/images/living-images/icons/pause.svg"
          alt="Pause"
          width="36"
          height="36"
          v-if="activeLivingImage && activeLivingImage.data.playing"
        > -->
        <v-icon v-if="activeLivingImage && activeLivingImage.data.playing">pause</v-icon>
        <!-- <img
          src="@/assets/images/living-images/icons/play_arrow.svg"
          alt="Play"
          width="36"
          height="36"
          v-else
        > -->
        <v-icon v-else>play_arrow</v-icon>
      </v-btn>

      <!-- mute/unmute button -->
      <v-btn
        @click="onMuteBtn"
        id="mute-button"
        class="bottom-bar-btn"
        :class="{'muted': muted, 'pulse': !muteBtnPressed}"
        fab
      >
        <!-- <img
          src="@/assets/images/living-images/icons/volume_off.svg"
          alt="Muted"
          width="20"
          height="20"
          v-if="muted"
        > -->
        <v-icon v-if="muted">volume_off</v-icon>
        <!-- <img
          src="@/assets/images/living-images/icons/volume_up.svg"
          alt="Unmuted"
          width="20"
          height="20"
          v-else
        > -->
        <v-icon v-else>volume_up</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import 'aframe'
import detectRTC from 'detectrtc'
import { lt } from 'semver'
import '@/aframe/components/common/clickable'
import '@/aframe/components/livingimages/livingimage'
import LivingImage from '@/classes/livingimage'
import ModalNotSupported from '@/components/ModalNotSupported.vue'

export default {
  name: 'LivingImages',
  components: {
    ModalNotSupported
  },
  data() {
    return {
      notSupported: {
        state: false,
        showModal: false,
        title: 'Upps, falscher Browser!',
        text: 'Es sieht so aus, als würdest du einen Browser benutzen, der unsere Inhalte nicht unterstützt.'
      },
      arjsLoaded: false,
      cameraLoaded: false,
      muted: true,
      muteBtnPressed: false,
      fadeTime: 1500, // milliseconds
      consoleMessages: [],
      livingImages: [
        new LivingImage(
          '47E53F2F010CCECE2B2EE9032433DCBB',
          'Vorstellung holoNative',
          'https://web.holonative.de/livingimages/vorstellung.patt',
          'https://web.holonative.de/livingimages/vorstellung.mp4'
        ),
        new LivingImage(
          '1a7f18a71435b9b2605252f37153b298',
          'Vorstellung holoNative türkis',
          'https://web.holonative.de/livingimages/vorstellung_tuerkis.patt',
          'https://web.holonative.de/livingimages/vorstellung.mp4'
        ),
        new LivingImage(
          'FD0A726090A63CF276A9DD45886F20F4',
          'KVG-Busfahrer',
          'https://web.holonative.de/livingimages/busfahrer.patt',
          'https://web.holonative.de/livingimages/busfahrer.mp4'
        )
      ]
    }
  },
  computed: {
    activeLivingImage() {
      if (this.livingImages.length === 0 || !this.livingImages[0].data) return
      return this.livingImages.find(livingImage => {
        return livingImage.data.active
      })
    },
    isIOSWebView() {
      // getUserMedia only works with Safari on iOS
      const ua = window.navigator.userAgent
      const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
      if (!iOS) return false
      const isBrave = !!(navigator.brave) || false
      const isChrome = !!ua.match(/CriOS/i)
      const isFirefox = !!ua.match(/FxiOS/i)
      return !ua.match(/Safari/i) || isBrave || isChrome || isFirefox
    },
    isIOSWebAppWrongVersion() {
      // getUserMedia only works as iOS-PWA with iOS 13.4.1 and higher
      const iOS = !!window.navigator.userAgent.match(/iPad/i) ||
        !!window.navigator.userAgent.match(/iPhone/i)
      return iOS &&
        window.matchMedia('(display-mode: standalone)').matches &&
        lt(this.detectRTC.osVersion, '13.4.1')
    },
  },
  watch: {
    consoleMessages() {
      /* setTimeout(() => {
        this.$refs['console-content'].scrollTop = this.$refs['console-content'].scrollHeight
      }, 50) */
    }
  },
  methods: {
    onAssetsLoaded() {
      console.log('Assets loaded')
      this.consoleMessages.push('Assets loaded')

      // add additional properties and event listeners to living images here
      for (let livingImage of this.livingImages) {
        // assign elements to living image objects
        livingImage.element = this.getElement('a', livingImage.id)
        livingImage.videoElement = this.getElement('v', livingImage.id)
        livingImage.markerElement = this.getElement('m', livingImage.id)
        livingImage.loadingElement = this.getElement('l', livingImage.id)

        // sync vue object data with a-frame component data by setting reference
        livingImage.element.addEventListener('loaded', e => {
          console.log('LI Component loaded')
          livingImage.data = e.target.components['living-image'].data
        })

        // listen for console logs
        livingImage.element.addEventListener('consolemessage', msg => {
          console.log(msg.detail)
          this.consoleMessages.push(msg.detail)
        })
      }
    },
    onAssetsTimeout(err) {
      console.error('Zeitüberschreitung beim Laden der Assets', err.message)
      this.consoleMessages.push('Zeitüberschreitung beim Laden der Assets' + err.message)
    },
    // called on play button click
    onPlayBtn() {
      if (!this.activeLivingImage) return
      this.activeLivingImage.element.setAttribute('living-image', 'userPaused', !this.activeLivingImage.data.userPaused)
    },
    // called on mute button click
    onMuteBtn() {
      if (!this.muteBtnPressed) this.muteBtnPressed = true
      this.muted = !this.muted
    },
    getElement(prefix, id) {
      // prefixes: m = marker, v = video, a = living-image, l = loading overlay
      return document.getElementById(`${prefix}-${id}`)
    }
  },
  created() {
    if (!detectRTC.isGetUserMediaSupported) {
      if (this.isIOSWebAppWrongVersion) {
        this.notSupported.title = "Upps, falsche iOS-Version!"
        this.notSupported.text = "Die Living Images werden von Ihrem Betriebssystem leider nicht unterstützt. Bitte aktualisieren Sie ihre iOS-Version."
      } else if (this.isIOSWebView) {
        this.notSupported.text = 'Die Living Images funktionieren auf Ihrem Gerät nur mit dem Safari-Browser. Bitte rufen Sie diese Seite damit erneut auf.'
      }
      this.notSupported.showModal = true
      this.notSupported.state = true
    }

  },
  async mounted() {
    if (this.notSupported.state) return
    setTimeout(() => {
      // attach arjs script to document
      let arjsScript = document.createElement('script')
      arjsScript.id = 'arjs-script'
      arjsScript.onload = () => {
        this.arjsLoaded = true
      }
      arjsScript.setAttribute('src', '/scripts/aframe-ar.js') // regularly look for updates!
      document.head.appendChild(arjsScript)
    }, 1000)

    // hide loading screen when camera loaded
    window.addEventListener('arjs-video-loaded', () => {
      this.cameraLoaded = true
    })
  }
}
</script>

<style>
body, #app {
  background: none !important;
  overflow: hidden !important;
  height: 100%;
}
#living-images {
  height: 100%;
  position: relative
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

#ar-wrapper {
  height: 100%;
  position: relative;
}
#living-images .v-btn {
  background-color: var(--v-dark-base);
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0;
}
#living-images .v-btn:hover, .v-btn:focus, .v-btn.focus {
  box-shadow: none;
}
#living-images .v-btn:active:not(.v-btn--disabled) {
  box-shadow: 0px 0px 11px 1px rgba(255, 255, 255, 0.5) !important;
}
#living-images .v-btn--disabled {
  background-color: rgb(46, 49, 49, 0.5) !important;
}
#play-button {
  width: 60px;
  height: 60px;
}
#mute-button {
  width: 46px;
  height: 46px;
}
#mute-button * {
  pointer-events: none;
}
.muted {
  background-color: var(--v-pink-base) !important;
}
.pulse {
  animation: pulse 1s infinite;
}
@keyframes pulse {
  50% {
     transform:scale(1.3);
  }
}
#bottom-bar {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display:flex;
  align-items: center;
  justify-content: center;
}
.bottom-bar-btn {
  margin: 0 10px;
}
#console {
  width: 80vw;
  background-color: rgba(0, 48, 100, 0.7);
  padding: 8px;
  margin-bottom: 10px;
}
#console-content {
  color: #fff;
  height: 20vh;
  overflow-y: auto;
  border-bottom: 1px solid #ccc;
  margin-bottom: 8px;
  padding-bottom: 5px;
}
/* .video-thumbnail {
  position: relative;
  display: inline-block;
  cursor: pointer;
  margin: 30px;
}
.video-thumbnail:before {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  -webkit-transform: translate(-50%, -50%);
  content: "\f01d";
  font-size: 100px;
  color: #fff;
  opacity: 0.8;
  text-shadow: 0px 0px 30px rgba(0, 0, 0, 0.5);
}
.video-thumbnail:hover:before {
  color: #eee;
} */
#ar-viewer button:focus {
  box-shadow: none !important;
}
</style>
